import { Chip, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import {
  ChargeCategory,
  ChargeDto,
  ChargeGetPaginatedDto,
  ChargeType,
} from "@/core/api/generated/v1";

import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";

export interface ChargeAutocompleteOption extends BaseAutocompleteOption<ChargeDto> {}

export const getChargeOptionTitle = (data: ChargeDto): string =>
  StringHelper.join([data.externalNumber || data.localNumber, data.name], ", ", {
    skipEmpty: true,
  });

export const chargeToOption = (data: ChargeDto): ChargeAutocompleteOption => ({
  id: data.id!,
  title: getChargeOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface ChargeAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<ChargeDto> {
  searchFilters?: Partial<Omit<ChargeGetPaginatedDto, "search" | "includeIds">>;
}

export default function ChargeAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: ChargeAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={chargeToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.chargesApi.apiV1ChargesGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          chargeGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          chargeGetPaginatedDto: {
            ...params.chargeGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Charge'
      placeholder='Search for charge...'
      renderOption={(props, option: ChargeAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='charge' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body1'>
                      {option.data && option.data.type !== ChargeType.None && (
                        <Chip
                          size='small'
                          color='default'
                          variant={"outlined"}
                          label={<InlineApiEnumValue type='ChargeType' value={option.data.type} />}
                          sx={{ mr: 1 }}
                        />
                      )}
                      {option.data && option.data.category !== ChargeCategory.None && (
                        <Chip
                          size='small'
                          color='default'
                          variant={"outlined"}
                          label={
                            <InlineApiEnumValue
                              type='ChargeCategory'
                              value={option.data.category}
                            />
                          }
                          sx={{ mr: 1 }}
                        />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
